// src/utils/regionFormatter.ts

import { DivIcon } from "leaflet";

/**
 * Canonical list of US states (lowercase, no spaces)
 * Used for robust prefix matching.
 */
const US_STATES = [
    "alabama", "alaska", "arizona", "arkansas", "california",
    "colorado", "connecticut", "delaware", "florida", "georgia",
    "hawaii", "idaho", "illinois", "indiana", "iowa",
    "kansas", "kentucky", "louisiana", "maine", "maryland",
    "massachusetts", "michigan", "minnesota", "mississippi", "missouri",
    "montana", "nebraska", "nevada", "newhampshire", "newjersey",
    "newmexico", "newyork", "northcarolina", "northdakota",
    "ohio", "oklahoma", "oregon", "pennsylvania", "rhodeisland",
    "southcarolina", "southdakota", "tennessee", "texas",
    "utah", "vermont", "virginia", "washington",
    "westvirginia", "wisconsin", "wyoming",
];

/**
 * Capitalizes the first letter of each word
 */
function titleCase(str: string): string {
    return str.replace(/\b\w/g, c => c.toUpperCase());
}

/**
 * Inserts spaces into long lowercase strings using simple heuristics
 * (fallback only – state detection is the primary mechanism)
 */
function humanizeRemainder(str: string): string {
    return titleCase(
        str.replace(/([a-z])([A-Z])/g, "$1 $2").trim()
    );
}

/**
 * Formats a backend region key into a user-friendly label.
 *
 * Examples:
 *  - "florida" → "Florida"
 *  - "californialaketahoe" → "California · Lake Tahoe"
 *  - "newyorkmanhattan" → "New York · Manhattan"
 */
export function formatRegionName(regionKey?: string): string {
    if (!regionKey) return "—";

    const raw = regionKey.toLowerCase();

    // Sort longest first to avoid partial matches
    const matchedState = [...US_STATES]
        .sort((a, b) => b.length - a.length)
        .find(state => raw.startsWith(state));

    // No state match — fallback
    if (!matchedState) {
        return titleCase(regionKey);
    }

    const remainder = raw.slice(matchedState.length);

    const prettyState = titleCase(
        matchedState.replace(
            /^(new|north|south|west)/,
            match => `${match} `
        )
    );

    if (!remainder) {
        return prettyState;
    }

    const prettyRegion = humanizeRemainder(remainder);

    return `${prettyState} - ${prettyRegion}`;
}

export function formatTimestamp(utcString: string): string {
    const date = new Date(utcString);

    const pad = (num: number, size = 2): string => String(num).padStart(size, '0');

    const year = date.getFullYear();
    const month = pad(date.getMonth() + 1); // months are 0-based
    const day = pad(date.getDate());

    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());
    const seconds = pad(date.getSeconds());
    const ms = pad(date.getMilliseconds(), 3); // milliseconds

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}.${ms}`;
}


// Map age → RGB color (Red = recent, Green = old)
export function getRGBColor(
    hoursAgo: number,
    maxHours: number
): string {
    // Clamp between 0–1
    const t = Math.min(Math.max(hoursAgo / maxHours, 0), 1);
    
    // t = 0 (recent) → red (255, 0, 0)
    // t = 1 (old) → green (0, 255, 0)
    const red = Math.round(255 * (1 - t));
    const green = Math.round(255 * t);
    const blue = 0;
    
    return `rgb(${red}, ${green}, ${blue})`;
}

export function createLightningDivIcon(
    polarity: number,
    hoursAgo: number,
    maxHours: number
): DivIcon {
    const isPositive = polarity >= 0;
    const color = getRGBColor(hoursAgo, maxHours);

    return new DivIcon({
        className: "lightning-div-icon",
        html: `
            <div style="
                color: ${color};
                font-size: 22px;
                line-height: 1;
                text-align: center;
                width: 100%;
                height: 100%;
                display: flex;
                align-items: center;
                justify-content: center;
                text-shadow: 
                    -1px -1px 0 #000,
                    1px -1px 0 #000,
                    -1px 1px 0 #000,
                    1px 1px 0 #000,
                    0 0 3px rgba(0,0,0,0.5);
            ">
                ${isPositive ? "+" : "−"}
            </div>
        `,
        iconSize: [15, 15],
        iconAnchor: [7.5, 7.5], // Center the smaller icon
    });
}